/**
 * OrgShell — sidebar layout for organization-tier routes (`/o/:orgSlug/*`).
 *
 * NAV-4-1: Mirrors the workspace AppShell's sidebar approach but uses
 * ORG_NAV_GROUPS and the `/o/:orgSlug/` prefix. Organization routes do
 * NOT go through WorkspaceGate (they use organizationContext on the
 * server). The shell reads :orgSlug from the URL and provides an org-
 * flavored sidebar with the same DS <Sidebar*> components.
 *
 * Mobile support reuses the same drawer/header/tabbar patterns from
 * AppShell, adapted for the org tier.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Drawer,
  Sidebar,
  SidebarGroup,
  SidebarItem,
  SidebarTopLevel,
  WorkspacePill,
} from '@aigovops/design-system';
import { useAuth } from '../../lib/auth';
import { useAlertsCount } from '../../hooks/useAlertsCount';
import { useOrgSupplierCount } from '../../hooks/useOrgSupplierCount';
import { navIcon } from './nav-icons';
import {
  ORG_NAV_GROUPS,
  findActiveItem,
  type NavGroup,
  type NavTopLevel,
  type NavItem,
} from '../../lib/nav-config';
import { useSidebarState } from '../../lib/sidebar-state';
import { useIsMobile } from './AppShell';

// -----------------------------------------------------------------------
// Badge resolution (org-tier)
// -----------------------------------------------------------------------

type BadgeSource = 'alerts' | 'my-work' | 'evidence-requests' | 'disputes' | 'questionnaires' | 'suppliers' | 'monitoring';

function useOrgBadgeCounts(orgSlug: string | undefined) {
  const alertsCount = useAlertsCount(undefined);
  const supplierCount = useOrgSupplierCount(orgSlug);
  return useMemo(
    () => ({
      resolve(source: BadgeSource | undefined): number | undefined {
        if (!source) return undefined;
        if (source === 'alerts') return alertsCount > 0 ? alertsCount : undefined;
        if (source === 'suppliers') return supplierCount > 0 ? supplierCount : undefined;
        return undefined;
      },
      alertsCritical: alertsCount,
    }),
    [alertsCount, supplierCount],
  );
}

// -----------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return 'U';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function shortDisplayName(user: { email?: string | null; name?: string | null }): string {
  if (user.name) {
    const parts = user.name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1][0]}.`;
  }
  const email = user.email ?? 'user';
  return email.split('@')[0];
}

// -----------------------------------------------------------------------
// OrgSidebarNav
// -----------------------------------------------------------------------

function OrgSidebarNav({
  prefix,
  badges,
  user,
  settingsActive,
  orgSlug,
  onNavigate,
}: {
  prefix: string;
  badges: ReturnType<typeof useOrgBadgeCounts>;
  user: { email?: string | null; name?: string | null; role?: string } | null;
  settingsActive: boolean;
  orgSlug: string;
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const loc = useLocation();

  const appPrefix = `${prefix}/`;
  const relativePath = loc.pathname.startsWith(appPrefix)
    ? loc.pathname.slice(appPrefix.length)
    : '';

  const allGroups = ORG_NAV_GROUPS;
  const activeResult = findActiveItem(allGroups, relativePath);
  const sidebar = useSidebarState(
    `org:${orgSlug}`,
    allGroups,
    activeResult?.groupId ?? null,
  );

  const displayName = user ? shortDisplayName(user) : 'User';
  const avatarInitials = user
    ? initialsOf(user.name || user.email || 'User')
    : 'U';

  const handleNav = useCallback(
    (href: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      navigate(href);
      onNavigate?.();
    },
    [navigate, onNavigate],
  );

  return (
    <Sidebar
      header={
        <div style={{ padding: 'var(--sp-2) var(--sp-3)' }}>
          <WorkspacePill
            label={orgSlug}
            swatch="ORG"
            expanded={false}
            onClick={() => {/* no switcher for org tier yet */}}
          />
        </div>
      }
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {/* Settings link */}
          <a
            href={`${prefix}/settings`}
            onClick={handleNav(`${prefix}/settings`)}
            aria-current={settingsActive ? 'page' : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              padding: '0 var(--sp-1)',
              minHeight: 44,
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--fs-sm)',
              fontFamily: 'inherit',
              textDecoration: 'none',
              color: settingsActive ? 'var(--accent)' : 'var(--text-muted)',
              background: settingsActive ? 'var(--accent-soft)' : 'transparent',
              fontWeight: settingsActive ? 500 : 400,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 18,
                height: 18,
                flexShrink: 0,
                color: settingsActive ? 'var(--accent)' : 'var(--text-dim)',
              }}
            >
              {navIcon('settings')}
            </span>
            Settings
          </a>

          {/* User identity with Org Admin tag */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              padding: '0 var(--sp-1)',
              minHeight: 32,
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: '50%',
                fontSize: 'var(--fs-xs)',
                fontWeight: 600,
                letterSpacing: '0.02em',
                background: 'var(--accent)',
                color: 'var(--text-inverse)',
                flexShrink: 0,
              }}
              aria-hidden="true"
            >
              {avatarInitials}
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 'var(--fs-sm)',
                  fontWeight: 500,
                  color: 'var(--text)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayName}
              </div>
              <div
                style={{
                  fontSize: 'var(--fs-xs)',
                  color: 'var(--text-muted)',
                }}
              >
                Org Admin
              </div>
            </div>
          </div>
        </div>
      }
    >
      {allGroups.map((entry) => {
        if ('topLevel' in entry && entry.topLevel) {
          const tl = entry as NavTopLevel;
          const href = `${prefix}/${tl.href}`;
          const isActive = activeResult?.itemId === tl.id;
          const badge = badges.resolve(tl.badge);
          return (
            <SidebarTopLevel
              key={tl.id}
              href={href}
              icon={navIcon(tl.icon)}
              label={tl.label}
              active={isActive}
              badge={badge}
              disabled={tl.disabled}
              disabledLabel={tl.disabledLabel}
              onClick={handleNav(href)}
            />
          );
        }

        const group = entry as NavGroup;
        return (
          <SidebarGroup
            key={group.id}
            id={group.id}
            label={group.label}
            expanded={sidebar.isExpanded(group.id)}
            onToggle={() => sidebar.toggleGroup(group.id)}
          >
            {group.items.map((item: NavItem) => {
              const href = `${prefix}/${item.href}`;
              const isActive = activeResult?.itemId === item.id;
              const badge = badges.resolve(item.badge);
              return (
                <SidebarItem
                  key={item.id}
                  href={href}
                  icon={navIcon(item.icon)}
                  label={item.label}
                  active={isActive}
                  badge={badge}
                  disabled={item.disabled}
                  disabledLabel={item.disabledLabel}
                  onClick={handleNav(href)}
                />
              );
            })}
          </SidebarGroup>
        );
      })}
    </Sidebar>
  );
}

// -----------------------------------------------------------------------
// Mobile Drawer (org)
// -----------------------------------------------------------------------

function OrgMobileDrawer({
  onClose,
  prefix,
  badges,
  user,
  settingsActive,
  orgSlug,
}: {
  onClose: () => void;
  prefix: string;
  badges: ReturnType<typeof useOrgBadgeCounts>;
  user: { email?: string | null; name?: string | null; role?: string } | null;
  settingsActive: boolean;
  orgSlug: string;
}) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    return () => { document.body.style.overflow = previous; };
  }, []);

  return (
    <>
      <div
        data-drawer-overlay
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--overlay-bg)',
          zIndex: 50,
        }}
        aria-hidden="true"
      />
      <style>{'.org-nav-drawer{left:0!important;right:auto!important;width:min(280px,100svw)!important;z-index:51!important;padding:0!important;border-left:0!important;display:flex;flex-direction:column;overscroll-behavior:contain}'}</style>
      <Drawer open onClose={onClose} label="Organization navigation menu" forceDialog className="org-nav-drawer">
        <button
          ref={closeButtonRef}
          onClick={onClose}
          aria-label="Close navigation menu"
          style={{
            position: 'absolute',
            top: 'var(--sp-2)',
            right: 'var(--sp-2)',
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            padding: 0,
            zIndex: 1,
          }}
        >
          {navIcon('x')}
        </button>
        <div style={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain' }}>
          <OrgSidebarNav
            prefix={prefix}
            badges={badges}
            user={user}
            settingsActive={settingsActive}
            orgSlug={orgSlug}
            onNavigate={onClose}
          />
        </div>
      </Drawer>
    </>
  );
}

// -----------------------------------------------------------------------
// Mobile Header (org)
// -----------------------------------------------------------------------

function OrgMobileHeader({
  onMenuOpen,
  activeLabel,
  alertsCount,
  prefix,
}: {
  onMenuOpen: () => void;
  activeLabel: string;
  alertsCount: number;
  prefix: string;
}) {
  const navigate = useNavigate();

  return (
    <header
      data-mobile-header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 40,
        height: 48,
        paddingTop: 'env(safe-area-inset-top, 0px)',
        background: 'var(--surface-elevated)',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingLeft: 'var(--sp-1)',
        paddingRight: 'var(--sp-1)',
      }}
    >
      <button
        onClick={onMenuOpen}
        aria-label="Open navigation menu"
        style={{
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          color: 'var(--text)',
          padding: 0,
          flexShrink: 0,
        }}
      >
        {navIcon('menu')}
      </button>
      <span
        style={{
          flex: 1,
          textAlign: 'center',
          fontSize: 'var(--fs-md)',
          fontWeight: 600,
          color: 'var(--text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: '0 var(--sp-1)',
        }}
      >
        {activeLabel}
      </span>
      <button
        onClick={() => navigate(`${prefix}/alerts`)}
        aria-label={alertsCount > 0 ? `${alertsCount} alerts` : 'Alerts'}
        style={{
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          color: 'var(--text-muted)',
          padding: 0,
          flexShrink: 0,
          position: 'relative',
        }}
      >
        {navIcon('bell')}
        {alertsCount > 0 ? (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              minWidth: 16,
              height: 16,
              padding: '0 4px',
              borderRadius: 999,
              background: 'var(--band-red-mark)',
              color: 'var(--text-inverse)',
              fontSize: 9,
              fontWeight: 700,
              lineHeight: '16px',
              textAlign: 'center',
            }}
          >
            {alertsCount}
          </span>
        ) : null}
      </button>
    </header>
  );
}

// -----------------------------------------------------------------------
// OrgShell
// -----------------------------------------------------------------------

export function OrgShell() {
  const { user } = useAuth();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const loc = useLocation();
  const isMobile = useIsMobile();

  const prefix = `/o/${orgSlug ?? ''}`;
  const badges = useOrgBadgeCounts(orgSlug);

  const settingsActive = loc.pathname.startsWith(`${prefix}/settings`);

  // Mobile state
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    setDrawerOpen(false);
  }, [loc.pathname]);

  // Active label for mobile header
  const appPrefix = `${prefix}/`;
  const relativePath = loc.pathname.startsWith(appPrefix)
    ? loc.pathname.slice(appPrefix.length)
    : '';

  const activeResult = findActiveItem(ORG_NAV_GROUPS, relativePath);

  const activeLabel = useMemo(() => {
    if (!activeResult) {
      if (settingsActive) return 'Org Settings';
      return 'Organization';
    }
    for (const entry of ORG_NAV_GROUPS) {
      if ('topLevel' in entry && entry.topLevel) {
        if (entry.id === activeResult.itemId) return entry.label;
        continue;
      }
      const group = entry as NavGroup;
      for (const item of group.items) {
        if (item.id === activeResult.itemId) return item.label;
      }
    }
    return 'Organization';
  }, [activeResult, settingsActive]);

  if (isMobile) {
    return (
      <div
        data-org-shell-root
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100dvh',
          background: 'var(--surface)',
          color: 'var(--text)',
        }}
      >
        <a
          href="#main-content"
          className="skip-link"
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          }}
        >
          Skip to main content
        </a>

        <OrgMobileHeader
          onMenuOpen={() => setDrawerOpen(true)}
          activeLabel={activeLabel}
          alertsCount={badges.alertsCritical}
          prefix={prefix}
        />

        <main
          id="main-content"
          tabIndex={-1}
          style={{
            flex: 1,
            padding: 'var(--sp-4)',
          }}
        >
          <Outlet />
        </main>

        {drawerOpen ? (
          <OrgMobileDrawer
            onClose={() => setDrawerOpen(false)}
            prefix={prefix}
            badges={badges}
            user={user}
            settingsActive={settingsActive}
            orgSlug={orgSlug ?? ''}
          />
        ) : null}
      </div>
    );
  }

  // Desktop layout
  return (
    <div
      data-org-shell-root
      style={{
        display: 'flex',
        minHeight: '100dvh',
        background: 'var(--surface)',
        color: 'var(--text)',
      }}
    >
      <div data-sidebar-sticky style={{ flexShrink: 0, position: 'sticky', top: 0 }}>
        <OrgSidebarNav
          prefix={prefix}
          badges={badges}
          user={user}
          settingsActive={settingsActive}
          orgSlug={orgSlug ?? ''}
        />
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <a
          href="#main-content"
          className="skip-link"
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          }}
        >
          Skip to main content
        </a>

        <main
          id="main-content"
          tabIndex={-1}
          style={{
            flex: 1,
            padding: 'var(--sp-5) max(var(--sp-5), env(safe-area-inset-right)) var(--sp-7) max(var(--sp-5), env(safe-area-inset-left))',
            maxWidth: 1360,
            margin: '0 auto',
            width: '100%',
          }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
